import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Paper,
  Stack,
  Autocomplete,
} from '@mui/material';
import { UserAutocomplete } from './shared/UserAutocomplete';
import { PageTitle } from './PageTitle';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Assignment as TaskIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, useGetList } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { EventInput, DateSelectArg, EventClickArg, EventResizeDoneArg } from '@fullcalendar/core';
import { formatHoursToHHMM } from './HoursMinutesInput';
import { useTenant } from '../contexts/TenantContext';

interface TaskEvent {
  id?: string;
  title: string;
  scheduledDate: string;
  dueDate?: string;
  duration?: number;
  priority: 'Low' | 'Medium' | 'High' | 'Critical';
  status: 'Todo' | 'InProgress' | 'InReview' | 'Blocked' | 'Done' | 'Cancelled';
  description?: string;
  estimatedHours?: number;
  assigneeIds?: string[];
  categoryId?: string;
  boardId?: string;
  assignees?: {
    id: string;
    name: string;
    avatar?: string;
  }[];
  assignee?: { // Keeping for backward compatibility
    id: string;
    name: string;
  };
  category?: {
    id: string;
    name: string;
    color: string;
  };
  board?: {
    id: string;
    name: string;
    type: string;
  };
}

// Production-safe function to ensure drag styles are always applied
const ensureDragStyles = () => {
  // Check if styles are already injected
  if (document.getElementById('fullcalendar-drag-styles')) return;

  const style = document.createElement('style');
  style.id = 'fullcalendar-drag-styles';
  style.textContent = `
    .fc-event {
      cursor: grab !important;
      user-select: none !important;
      -webkit-user-select: none !important;
    }
    .fc-event:hover {
      cursor: grab !important;
    }
    .fc-event.fc-event-dragging {
      cursor: grabbing !important;
      opacity: 0.7 !important;
      z-index: 999 !important;
    }
  `;
  document.head.appendChild(style);
};

export const TaskCalendar = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const [events, setEvents] = useState<EventInput[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskEvent | null>(null);
  const [tasks, setTasks] = useState<TaskEvent[]>([]);
  const [boards, setBoards] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<string>('');
  const [formData, setFormData] = useState<TaskEvent>({
    title: '',
    scheduledDate: '',
    dueDate: '',
    duration: 1,
    priority: 'Medium',
    status: 'Todo',
    description: '',
    estimatedHours: 1,
    assigneeIds: [],
    categoryId: '',
    boardId: '',
  });
  const [calendarView, setCalendarView] = useState(() => {
    // Get saved view from localStorage or default to timeGridWeek
    return localStorage.getItem('calendarViewMode') || 'timeGridWeek';
  });

  // Get tenant settings for calendar business hours
  const {
    data: tenantSettings,
    isLoading: isLoadingTenantSettings,
    error: tenantSettingsError,
  } = useGetList('crm/tenant-settings', {
    pagination: { page: 1, perPage: 1 },
  });

  const currentTenantSettings = tenantSettings?.[0];

  useEffect(() => {
    // Ensure drag styles are applied in production
    ensureDragStyles();

    console.log('🚀 [CALENDAR] Component initializing...', {
      timestamp: new Date().toISOString(),
      tenant: currentTenant?.code,
    });
    loadTasks();
    loadBoards();
    loadUsers();
    loadCategories();
  }, [currentTenant?.code]); // Reload calendar data when tenant changes

  // Log tenant settings when they change
  useEffect(() => {
    if (currentTenantSettings) {
      console.log('⚙️ [CALENDAR] Tenant settings loaded for business hours:', {
        calendarStartTime: currentTenantSettings.calendarStartTime,
        calendarEndTime: currentTenantSettings.calendarEndTime,
        timeZone: currentTenantSettings.timeZone,
        timestamp: new Date().toISOString(),
      });
    }
  }, [currentTenantSettings]);

  useEffect(() => {
    console.log('🔄 [CALENDAR] Board filter changed, reloading tasks...', {
      selectedBoardId,
      boardName: selectedBoardId ? boards.find(b => b.id === selectedBoardId)?.name || selectedBoardId : 'All boards',
      tenant: currentTenant?.code,
    });
    loadTasks();
  }, [selectedBoardId, currentTenant?.code]); // Reload tasks when board or tenant changes

  const loadTasks = async () => {
    const startTime = performance.now();
    console.log('📥 [CALENDAR] Loading tasks...', {
      selectedBoardId,
      boardName: selectedBoardId ? boards.find(b => b.id === selectedBoardId)?.name : 'All boards',
      timestamp: new Date().toISOString(),
    });

    try {
      // Build filter to include only tasks with scheduled dates and optionally filter by board
      const filter: any = {
        scheduledDate: 'not-null', // Only tasks with scheduled dates
      };

      // If a specific board is selected, filter by it
      if (selectedBoardId) {
        filter.workflowId = selectedBoardId;
        console.log('🎯 [CALENDAR] Applying workflow filter:', { workflowId: selectedBoardId });
      }

      console.log('🔍 [CALENDAR] Query parameters:', {
        filter,
        sort: { field: 'scheduledDate', order: 'ASC' },
        pagination: { page: 1, perPage: 1000 },
      });

      const response = await dataProvider.getList('tasks', {
        filter,
        sort: { field: 'scheduledDate', order: 'ASC' },
        pagination: { page: 1, perPage: 1000 },
      });

      console.log('📊 [CALENDAR] Raw API response:', {
        totalTasks: response.data.length,
        tasksWithScheduledDate: response.data.filter((task: TaskEvent) => task.scheduledDate).length,
        sampleTasks: response.data.slice(0, 3).map((task: TaskEvent) => ({
          id: task.id,
          title: task.title,
          scheduledDate: task.scheduledDate,
          duration: task.duration,
          status: task.status,
          priority: task.priority,
        })),
      });

      const calendarEvents = response.data
        .filter((task: TaskEvent) => task.scheduledDate) // Additional client-side filter as backup
        .map((task: TaskEvent) => {
          const startDate = new Date(task.scheduledDate);
          const endDate = task.duration
            ? new Date(startDate.getTime() + task.duration * 60 * 60 * 1000)
            : new Date(startDate.getTime() + 60 * 60 * 1000);

          return {
            id: task.id,
            title: task.title,
            start: task.scheduledDate,
            end: endDate.toISOString(),
            backgroundColor: getTaskColor(task.status, task.priority),
            borderColor: getTaskBorderColor(task.priority),
            textColor: getTaskTextColor(task.status),
            extendedProps: {
              ...task,
              taskId: task.id,
              taskType: 'task',
            },
          };
        });

      setEvents(calendarEvents);
      setTasks(response.data);

      const loadTime = performance.now() - startTime;
      console.log('✅ [CALENDAR] Tasks loaded successfully:', {
        totalTasksFromAPI: response.data.length,
        calendarEventsCreated: calendarEvents.length,
        boardFilter: selectedBoardId ? boards.find(b => b.id === selectedBoardId)?.name || selectedBoardId : 'All boards',
        loadTimeMs: Math.round(loadTime),
        eventsDateRange: calendarEvents.length > 0 ? {
          earliest: calendarEvents.reduce((min, event) => event.start < min ? event.start : min, calendarEvents[0].start),
          latest: calendarEvents.reduce((max, event) => event.start > max ? event.start : max, calendarEvents[0].start),
        } : null,
      });
    } catch (error) {
      console.error('❌ [CALENDAR] Error loading tasks:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        selectedBoardId,
        timestamp: new Date().toISOString(),
      });
      notify('Failed to load tasks', { type: 'error' });
    }
  };

  const loadBoards = async () => {
    try {
      const response = await dataProvider.getList('workflows', {
        filter: {},
        sort: { field: 'name', order: 'ASC' },
        pagination: { page: 1, perPage: 100 },
      });
      setBoards(response.data);

      // Get last selected board from localStorage or use first board
      const lastSelectedBoardId = localStorage.getItem('lastSelectedCalendarBoardId');
      if (lastSelectedBoardId && response.data.some((board: any) => board.id === lastSelectedBoardId)) {
        setSelectedBoardId(lastSelectedBoardId);
      } else if (response.data.length > 0) {
        setSelectedBoardId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error loading boards:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await dataProvider.getList('task-categories', {
        filter: {},
        sort: { field: 'name', order: 'ASC' },
        pagination: { page: 1, perPage: 100 },
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await dataProvider.getList('users', {
        filter: {},
        sort: { field: 'name', order: 'ASC' },
        pagination: { page: 1, perPage: 100 },
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const getTaskColor = (status: string, priority?: string) => {
    // Base color on status
    let baseColor = '#757575';
    switch (status) {
      case 'Todo':
        baseColor = '#1976d2'; // Blue
        break;
      case 'InProgress':
        baseColor = '#ed6c02'; // Orange
        break;
      case 'InReview':
        baseColor = '#9c27b0'; // Purple
        break;
      case 'Blocked':
        baseColor = '#d32f2f'; // Red
        break;
      case 'Done':
        baseColor = '#2e7d32'; // Green
        break;
      case 'Cancelled':
        baseColor = '#616161'; // Grey
        break;
    }

    // Adjust intensity based on priority
    if (priority === 'Critical' || priority === 'High') {
      return baseColor; // Full intensity
    } else if (priority === 'Low') {
      return baseColor + '80'; // 50% opacity
    }
    return baseColor + 'CC'; // 80% opacity for medium
  };

  const getTaskBorderColor = (priority?: string) => {
    switch (priority) {
      case 'Critical':
        return '#ff1744'; // Bright red border
      case 'High':
        return '#ff9800'; // Orange border
      case 'Medium':
        return '#2196f3'; // Blue border
      case 'Low':
        return '#9e9e9e'; // Grey border
      default:
        return '#9e9e9e';
    }
  };

  const getTaskTextColor = (status: string) => {
    switch (status) {
      case 'Done':
        return '#ffffff';
      case 'Cancelled':
        return '#ffffff';
      default:
        return '#ffffff';
    }
  };

  const handleDateSelect = (selectInfo: DateSelectArg) => {
    setEditingTask(null);
    const endTime = selectInfo.endStr ||
      new Date(new Date(selectInfo.startStr).getTime() + 60 * 60 * 1000).toISOString();

    // Pre-select the currently selected board for the new task
    const defaultBoardId = selectedBoardId || '';

    setFormData({
      title: '',
      scheduledDate: selectInfo.startStr,
      dueDate: endTime,
      duration: 1,
      priority: 'Medium',
      status: 'Todo',
      description: '',
      estimatedHours: 1,
      assigneeIds: [],
      categoryId: '',
      boardId: defaultBoardId,
    });
    setDialogOpen(true);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const event = clickInfo.event;
    const extendedProps = event.extendedProps;

    // Navigate to task show page instead of opening dialog
    if (extendedProps.taskType === 'task' && extendedProps.taskId) {
      navigate(`/tasks/${extendedProps.taskId}/show`);
      return;
    }

    // Fallback to dialog for editing
    let initialAssigneeIds: string[] = [];
    if (extendedProps.assigneeIds && Array.isArray(extendedProps.assigneeIds)) {
      initialAssigneeIds = extendedProps.assigneeIds;
    } else if (extendedProps.assignees && Array.isArray(extendedProps.assignees)) {
      initialAssigneeIds = extendedProps.assignees.map((u: any) => u.id);
    } else if (extendedProps.assigneeId) {
      initialAssigneeIds = [extendedProps.assigneeId];
    } else if (extendedProps.assignee?.id) {
      initialAssigneeIds = [extendedProps.assignee.id];
    }

    const taskData: TaskEvent = {
      id: event.id,
      title: event.title,
      scheduledDate: event.startStr,
      dueDate: extendedProps.dueDate,
      duration: extendedProps.duration,
      priority: extendedProps.priority,
      status: extendedProps.status,
      description: extendedProps.description,
      estimatedHours: extendedProps.estimatedHours,
      assigneeIds: initialAssigneeIds,
      categoryId: extendedProps.categoryId,
      boardId: extendedProps.boardId,
      assignee: extendedProps.assignee,
      category: extendedProps.category,
      board: extendedProps.board,
    };

    setEditingTask(taskData);
    setFormData(taskData);
    setDialogOpen(true);
  };

  // Handle drag and drop for rescheduling tasks
  const handleEventChange = async (changeInfo: any) => {
    const { event } = changeInfo;
    const extendedProps = event.extendedProps;

    console.log('🔄 [CALENDAR] Task change detected:', {
      taskId: extendedProps.taskId,
      taskTitle: event.title,
      taskType: extendedProps.taskType,
      newStart: event.startStr,
      newEnd: event.endStr,
    });

    if (extendedProps.taskType !== 'task') {
      console.log('⚠️ [CALENDAR] Skipping non-task event:', extendedProps.taskType);
      return;
    }

    try {
      const newScheduledDate = event.startStr;

      // Calculate new duration based on start and end times
      const startTime = new Date(event.start!);
      const endTime = new Date(event.end!);
      const rawDuration = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60); // Convert to hours
      const roundedDuration = Math.max(0.25, Math.round(rawDuration * 4) / 4); // Round to nearest 15 minutes, minimum 15 minutes

      console.log('📊 [CALENDAR] Duration calculation:', {
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        rawDurationHours: rawDuration,
        roundedDurationHours: roundedDuration,
      });

      const updateData = {
        scheduledDate: newScheduledDate,
        duration: roundedDuration,
      };

      console.log('🚀 [CALENDAR] Updating task with data:', updateData);

      await dataProvider.update('tasks', {
        id: extendedProps.taskId,
        data: updateData,
        previousData: extendedProps,
      });

      console.log('✅ [CALENDAR] Task successfully updated:', {
        taskId: extendedProps.taskId,
        taskTitle: event.title,
        updatedFields: updateData,
      });

      notify(`Task "${event.title}" rescheduled to ${new Date(newScheduledDate).toLocaleString()}`, { type: 'success' });
      loadTasks(); // Reload to get fresh data
    } catch (error) {
      console.error('❌ [CALENDAR] Failed to reschedule task:', {
        taskId: extendedProps.taskId,
        taskTitle: event.title,
        error: error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        stackTrace: error instanceof Error ? error.stack : undefined,
      });
      notify('Failed to reschedule task. Changes reverted.', { type: 'error' });
    }
  };

  // Handle event resize for changing duration
  const handleEventResize = async (resizeInfo: EventResizeDoneArg) => {
    const { event } = resizeInfo;
    const extendedProps = event.extendedProps;

    console.log('🔧 [CALENDAR] Task resize started:', {
      taskId: extendedProps.taskId,
      taskTitle: event.title,
      taskType: extendedProps.taskType,
      originalStart: resizeInfo.oldEvent.startStr,
      originalEnd: resizeInfo.oldEvent.endStr,
      newStart: event.startStr,
      newEnd: event.endStr,
      resizeDirection: event.end! > resizeInfo.oldEvent.end! ? 'extended' : 'shortened',
    });

    if (extendedProps.taskType !== 'task') {
      console.log('⚠️ [CALENDAR] Skipping non-task event resize:', extendedProps.taskType);
      return;
    }

    try {
      // Calculate new duration based on resized event
      const startTime = new Date(event.start!);
      const endTime = new Date(event.end!);
      const rawDuration = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60); // Convert to hours
      const roundedDuration = Math.max(0.25, Math.round(rawDuration * 4) / 4); // Round to nearest 15 minutes, minimum 15 minutes

      // Get original duration for comparison
      const originalDuration = extendedProps.duration || 0;
      const originalEndTime = new Date(resizeInfo.oldEvent.end!);
      const originalRawDuration = (originalEndTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

      console.log('⏱️ [CALENDAR] Duration resize calculation:', {
        originalRawDurationHours: originalRawDuration,
        originalDuration: originalDuration,
        newRawDurationHours: rawDuration,
        newRoundedDurationHours: roundedDuration,
        durationChange: roundedDuration - originalDuration,
        durationChangeMinutes: (roundedDuration - originalDuration) * 60,
        wasExtended: roundedDuration > originalDuration,
      });

      const updateData = {
        duration: roundedDuration,
      };

      console.log('🚀 [CALENDAR] Updating task duration with data:', updateData);

      await dataProvider.update('tasks', {
        id: extendedProps.taskId,
        data: updateData,
        previousData: extendedProps,
      });

      console.log('✅ [CALENDAR] Task duration successfully updated:', {
        taskId: extendedProps.taskId,
        taskTitle: event.title,
        oldDuration: originalDuration,
        newDuration: roundedDuration,
        changeInHours: roundedDuration - originalDuration,
      });

      notify(`Task "${event.title}" duration updated to ${formatHoursToHHMM(roundedDuration) || '00:00'}`, { type: 'success' });
      loadTasks(); // Reload to get fresh data
    } catch (error) {
      console.error('❌ [CALENDAR] Failed to update task duration:', {
        taskId: extendedProps.taskId,
        taskTitle: event.title,
        error: error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        stackTrace: error instanceof Error ? error.stack : undefined,
      });
      notify('Failed to update task duration. Changes reverted.', { type: 'error' });
      resizeInfo.revert(); // Revert the visual change
    }
  };

  const handleChange = (field: string) => (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      const taskData = {
        ...formData,
        // Ensure we have the minimum required fields
        scheduledDate: formData.scheduledDate,
        duration: formData.duration || 1,
        priority: formData.priority || 'Medium',
        status: formData.status || 'Todo',
      };

      if (editingTask) {
        await dataProvider.update('tasks', {
          id: editingTask.id,
          data: taskData,
          previousData: editingTask,
        });
        notify('Task updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('tasks', {
          data: taskData,
        });
        notify('Task created successfully', { type: 'success' });
      }

      setDialogOpen(false);
      loadTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      notify('Error saving task', { type: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!editingTask?.id) {
      return;
    }

    try {
      await dataProvider.delete('tasks', {
        id: editingTask.id,
      });
      notify('Task deleted successfully', { type: 'success' });
      setDialogOpen(false);
      loadTasks();
    } catch (error) {
      notify('Error deleting task', { type: 'error' });
    }
  };

  const taskStatuses = ['Todo', 'InProgress', 'InReview', 'Blocked', 'Done', 'Cancelled'];
  const taskPriorities = ['Low', 'Medium', 'High', 'Critical'];

  // Handle calendar view change and save to localStorage
  const handleViewChange = (view: any) => {
    const newView = view.view.type;
    setCalendarView(newView);
    localStorage.setItem('calendarViewMode', newView);
    console.log('📅 [CALENDAR] View changed:', {
      previousView: calendarView,
      newView: newView,
      timestamp: new Date().toISOString(),
    });
  };

  return (
    <Box>
      <PageTitle
        title="Task Calendar"
        subtitle="View and manage scheduled tasks with drag & drop rescheduling • Drag to reschedule • Resize to change duration"
        icon={<TaskIcon />}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, px: 3 }}>
        {/* Task Board Selector */}
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Task Board</InputLabel>
            <Select
              value={selectedBoardId}
              onChange={(e) => {
                const newBoardId = e.target.value;
                const previousBoardId = selectedBoardId;
                const newBoardName = newBoardId ? boards.find(b => b.id === newBoardId)?.name || newBoardId : 'All boards';
                const previousBoardName = previousBoardId ? boards.find(b => b.id === previousBoardId)?.name || previousBoardId : 'All boards';

                console.log('🎛️ [CALENDAR] Board filter changed:', {
                  previousBoardId,
                  previousBoardName,
                  newBoardId,
                  newBoardName,
                  timestamp: new Date().toISOString(),
                });

                setSelectedBoardId(newBoardId);
                localStorage.setItem('lastSelectedCalendarBoardId', newBoardId);
              }}
              label="Task Board"
            >
              <MenuItem value="">
                <em>All Boards</em>
              </MenuItem>
              {boards.length === 0 ? (
                <MenuItem disabled>
                  <em>No task boards available</em>
                </MenuItem>
              ) : (
                boards.map((board) => (
                  <MenuItem key={board.id} value={board.id}>
                    {board.name} ({board.type})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Stack>

        {/* Actions */}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingTask(null);
            // Pre-select the currently selected board for the new task
            const defaultBoardId = selectedBoardId || '';

            setFormData({
              title: '',
              scheduledDate: new Date().toISOString(),
              dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
              duration: 1,
              priority: 'Medium',
              status: 'Todo',
              description: '',
              estimatedHours: 1,
              assigneeIds: [],
              categoryId: '',
              boardId: defaultBoardId,
            });
            setDialogOpen(true);
          }}
        >
          Create Task
        </Button>
      </Stack>

      <Box sx={{ p: 3 }}>
        {events.length === 0 && (
          <Paper sx={{ p: 3, mb: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body1" color="text.secondary">
              {selectedBoardId
                ? 'No scheduled tasks found for this board.'
                : 'No tasks with scheduled dates found. Create tasks with scheduled dates to see them on the calendar.'
              }
            </Typography>
          </Paper>
        )}

        <Card>
          <CardContent
            sx={{
              '& .fc-event': {
                cursor: 'grab !important',
                borderRadius: '4px !important',
                border: '2px solid transparent !important',
                transition: 'all 0.2s ease !important',
                fontWeight: '500 !important',
                userSelect: 'none !important',
                WebkitUserSelect: 'none !important',
                '&:hover': {
                  cursor: 'grab !important',
                  opacity: '0.9 !important',
                  transform: 'translateY(-1px) !important',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2) !important',
                },
                '&.fc-event-dragging': {
                  cursor: 'grabbing !important',
                  opacity: '0.7 !important',
                  transform: 'scale(1.02) !important',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3) !important',
                  zIndex: '999 !important',
                },
                '&.fc-event-resizing': {
                  opacity: '0.8 !important',
                  border: '2px dashed #fff !important',
                },
              },
              '& .fc-event-title': {
                fontWeight: '600 !important',
                textShadow: '0 1px 2px rgba(0,0,0,0.1) !important',
              },
              '& .fc-event-time': {
                fontSize: '11px !important',
                opacity: '0.9 !important',
              },
              '& .task-event': {
                cursor: 'grab !important',
              },
            }}
          >
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
              }}
              initialView={calendarView}
              viewDidMount={handleViewChange}
              editable={true}
              selectable={true}
              selectMirror={true}
              droppable={true}
              dayMaxEvents={true}
              weekends={true}
              events={events}
              select={handleDateSelect}
              eventClick={handleEventClick}
              eventChange={handleEventChange}
              eventResize={handleEventResize}
              eventMouseEnter={(info) => {
                console.log('🐭 [CALENDAR] Mouse enter event:', {
                  eventId: info.event.id,
                  title: info.event.title,
                  isEditable: info.event.extendedProps?.taskType === 'task',
                  start: info.event.startStr,
                  end: info.event.endStr
                });
              }}
              eventDragStart={(info) => {
                console.log('🏁 [CALENDAR] Drag START detected:', {
                  eventId: info.event.id,
                  title: info.event.title,
                  startTime: info.event.startStr,
                  taskType: info.event.extendedProps?.taskType,
                  jsEvent: info.jsEvent.type
                });
              }}
              eventDragStop={(info) => {
                console.log('🛑 [CALENDAR] Drag STOP detected:', {
                  eventId: info.event.id,
                  title: info.event.title,
                  newStart: info.event.startStr,
                  jsEvent: info.jsEvent.type
                });
              }}
              height="auto"
              slotMinTime={currentTenantSettings?.calendarStartTime || "06:00:00"}
              slotMaxTime={currentTenantSettings?.calendarEndTime || "20:00:00"}
              slotDuration="00:15:00"
              snapDuration="00:15:00"
              businessHours={{
                daysOfWeek: [1, 2, 3, 4, 5, 6],
                startTime: currentTenantSettings?.calendarStartTime?.slice(0, 5) || '08:00',
                endTime: currentTenantSettings?.calendarEndTime?.slice(0, 5) || '18:00',
              }}
              eventDisplay="block"
              displayEventTime={true}
              allDaySlot={false}
              eventTextColor="#ffffff"
              eventConstraint={{
                startTime: currentTenantSettings?.calendarStartTime || '06:00:00',
                endTime: currentTenantSettings?.calendarEndTime || '22:00:00',
              }}
              eventOverlap={true}
              eventResizableFromStart={false}
              eventDurationEditable={true}
              eventStartEditable={true}
              eventDragMinDistance={5}
              dragRevertDuration={300}
              eventClassNames={(arg) => {
                return ['task-event'];
              }}
              eventDidMount={(info) => {
                // Add tooltip with task details
                const task = info.event.extendedProps;
                info.el.title = `${info.event.title}\n${task.status} • ${task.priority}\nDuration: ${formatHoursToHHMM(task.duration || 1)}\nAssigned to: ${task.assignee?.name || 'Unassigned'}`;

                // Production debugging: Check if drag is enabled
                if (process.env.NODE_ENV === 'production') {
                  const computedStyle = window.getComputedStyle(info.el);
                  console.log('🔍 [PROD-DEBUG] Event mounted:', {
                    eventId: info.event.id,
                    title: info.event.title,
                    isDraggable: info.event.startEditable,
                    isResizable: info.event.durationEditable,
                    cursor: computedStyle.cursor,
                    userSelect: computedStyle.userSelect,
                    pointerEvents: computedStyle.pointerEvents,
                    position: computedStyle.position,
                  });
                }
              }}
            />
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                {editingTask ? 'Edit Task' : 'Create New Task'}
              </Typography>
              <IconButton onClick={() => setDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Task Title"
                  value={formData.title}
                  onChange={handleChange('title')}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Board</InputLabel>
                  <Select
                    value={formData.boardId}
                    onChange={handleChange('boardId')}
                    label="Board"
                  >
                    {boards.map(board => (
                      <MenuItem key={board.id} value={board.id}>
                        {board.name} ({board.type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.categoryId}
                    onChange={handleChange('categoryId')}
                    label="Category"
                  >
                    {categories.map(category => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Scheduled Date & Time"
                  type="datetime-local"
                  value={formData.scheduledDate ? formData.scheduledDate.slice(0, 16) : ''}
                  onChange={e => {
                    const scheduledDate = new Date(e.target.value).toISOString();
                    setFormData(prev => ({
                      ...prev,
                      scheduledDate: scheduledDate,
                    }));
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Duration (hours)"
                  type="number"
                  value={formData.duration}
                  onChange={handleChange('duration')}
                  inputProps={{ min: 0.25, step: 0.25 }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Due Date & Time"
                  type="datetime-local"
                  value={formData.dueDate ? formData.dueDate.slice(0, 16) : ''}
                  onChange={e => {
                    const dueDate = new Date(e.target.value).toISOString();
                    setFormData(prev => ({
                      ...prev,
                      dueDate: dueDate,
                    }));
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Estimated Hours"
                  type="number"
                  value={formData.estimatedHours}
                  onChange={handleChange('estimatedHours')}
                  inputProps={{ min: 0.25, step: 0.25 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select value={formData.priority} onChange={handleChange('priority')} label="Priority">
                    {taskPriorities.map(priority => (
                      <MenuItem key={priority} value={priority}>
                        <Chip
                          label={priority}
                          color={priority === 'Critical' || priority === 'High' ? 'error' :
                            priority === 'Medium' ? 'warning' : 'default'}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        {priority}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={formData.status} onChange={handleChange('status')} label="Status">
                    {taskStatuses.map(status => (
                      <MenuItem key={status} value={status}>
                        <Chip
                          label={status}
                          color={status === 'Done' ? 'success' :
                            status === 'InProgress' ? 'warning' :
                              status === 'Blocked' ? 'error' : 'default'}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <UserAutocomplete
                  multiple
                  value={formData.assigneeIds}
                  onChange={(userIds) => setFormData(prev => ({ ...prev, assigneeIds: (userIds as string[]) || [] }))}
                  label="Assigned To"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={handleChange('description')}
                  multiline
                  rows={3}
                  placeholder="Task description and notes..."
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            {editingTask && (
              <Button
                onClick={handleDelete}
                color="error"
                startIcon={<DeleteIcon />}
                sx={{ mr: 'auto' }}
              >
                Delete
              </Button>
            )}
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              {editingTask ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

// Backward compatibility alias
export const ServiceCalendar = TaskCalendar;
